import * as React from 'react';
import { Alert, Avatar, Box, Button, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import InsightsIcon from '@mui/icons-material/Insights';
import { useNavigate } from 'react-router-dom';
import { getRecentVirtualPlayers, getVirtualCatalogLeagues, getVirtualCatalogSports, getVirtualProviderMatches, getVirtualProviderTeams } from '../Api';
import VirtualActionsDialog from '../components/virtual-sports/VirtualActionsDialog';
import VirtualLeaguesTable from '../components/virtual-sports/VirtualLeaguesTable';
import VirtualMatchEditDialog from '../components/virtual-sports/VirtualMatchEditDialog';
import VirtualMatchesTable from '../components/virtual-sports/VirtualMatchesTable';
import VirtualTeamsTable from '../components/virtual-sports/VirtualTeamsTable';
import VirtualPlayersTable from '../components/virtual-sports/VirtualPlayersTable';
import VirtualSettingsDialog from '../components/virtual-sports/VirtualSettingsDialog';
import VirtualStatisticsDialog from '../components/virtual-sports/VirtualStatisticsDialog';
import VirtualPlayerDetailsDialog from '../components/virtual-sports/VirtualPlayerDetailsDialog';
import VirtualTeamDetailsDialog from '../components/virtual-sports/VirtualTeamDetailsDialog';
import VirtualLeagueDetailsDialog from '../components/virtual-sports/VirtualLeagueDetailsDialog';
import VirtualActivityLogBell from '../components/virtual-sports/VirtualActivityLogBell';
import { useOpenVirtualMatch } from '../components/virtual-sports/useOpenVirtualMatch';
import { VirtualProviderMatchSummaryDto, VirtualProviderPlayerDto, VirtualProviderTeamDto } from '../models/dtos/VirtualProviderCatalogDto';
import {
    VirtualCatalogLeagueDto,
    VirtualCatalogSportDto,
} from '../models/dtos/VirtualSportsCatalogDto';

export default function VirtualSportsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [sports, setSports] = React.useState<VirtualCatalogSportDto[]>([]);
    const [leagues, setLeagues] = React.useState<VirtualCatalogLeagueDto[]>([]);
    const [matches, setMatches] = React.useState<VirtualProviderMatchSummaryDto[]>([]);
    const [totalMatches, setTotalMatches] = React.useState(0);
    const [teams, setTeams] = React.useState<VirtualProviderTeamDto[]>([]);
    const [players, setPlayers] = React.useState<VirtualProviderPlayerDto[]>([]);
    const [error, setError] = React.useState('');
    const [settingsOpen, setSettingsOpen] = React.useState(false);
    const [actionsOpen, setActionsOpen] = React.useState(false);
    const [statisticsOpen, setStatisticsOpen] = React.useState(false);
    const [selectedPlayer, setSelectedPlayer] = React.useState<VirtualProviderPlayerDto | null>(null);
    const [selectedTeam, setSelectedTeam] = React.useState<VirtualProviderTeamDto | null>(null);
    const [selectedLeagueSyncId, setSelectedLeagueSyncId] = React.useState<string | null>(null);
    const [editingMatch, setEditingMatch] = React.useState<VirtualProviderMatchSummaryDto | null>(null);
    const { openMatch, openingSyncId, error: openError, clearError } = useOpenVirtualMatch();

    React.useEffect(() => {
        let cancelled = false;
        Promise.all([
            getVirtualCatalogSports(),
            getVirtualCatalogLeagues(0, 10, 'MOST_RECENT'),
            getVirtualProviderMatches(0, 10),
            getVirtualProviderTeams(),
            getRecentVirtualPlayers(0, 10),
        ]).then(([sportItems, leaguePage, matchPage, teamItems, playerPage]) => {
            if (cancelled) return;
            setSports(sportItems);
            setLeagues(leaguePage.items);
            setMatches(matchPage.items);
            setTotalMatches(matchPage.totalItems);
            setTeams(teamItems.slice(0, 10));
            setPlayers(playerPage.items);
        }).catch(reason => {
            if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load virtual sports');
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    return (
        <Stack spacing={4} sx={{ mx: { xs: 2, md: 5 }, my: 5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h3">Virtual sports</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Virtual Football, Virtual Basketball and Virtual Tennis synchronized into the main BETUP catalog.
                    </Typography>
                </Box>
                <VirtualActivityLogBell />
                <Button variant="outlined" startIcon={<InsightsIcon />} onClick={() => setStatisticsOpen(true)}>
                    Statistics
                </Button>
                <Button variant="outlined" startIcon={<BuildIcon />} onClick={() => setActionsOpen(true)}>
                    Actions
                </Button>
                <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)}>
                    Settings
                </Button>
            </Stack>

            {loading ? <Box textAlign="center"><CircularProgress /></Box> : (
                <>
                    {error ? <Typography color="error">{error}</Typography> : null}
                    <Box>
                        <Typography variant="h5" sx={{ mb: 2 }}>Sports</Typography>
                        <Grid container spacing={2}>
                            {sports.map(sport => (
                                <Grid item xs={12} sm={6} md={4} key={sport.id}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar src={sport.photo_url ?? undefined} sx={{ width: 48, height: 48 }}>
                                                    {sport.name.slice(0, 1)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h6">{sport.name}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        ID {sport.id} · {sport.number_of_periods ?? '—'} periods
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h5" sx={{ flex: 1 }}>Recent leagues</Typography>
                                <Button onClick={() => navigate('/virtual-sports/leagues')}>View all</Button>
                            </Stack>
                            <VirtualLeaguesTable
                                leagues={leagues}
                                onViewMatches={league => navigate(`/virtual-sports/matches?leagueId=${league.sync_id ?? ''}`)}
                                onLeagueClick={league => setSelectedLeagueSyncId(league.sync_id ?? null)}
                            />
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h5" sx={{ flex: 1 }}>Recent players</Typography>
                                <Button onClick={() => navigate('/virtual-sports/players')}>View all</Button>
                            </Stack>
                            <VirtualPlayersTable
                                players={players}
                                sportNames={new Map(sports.map(sport => [sport.id, sport.name]))}
                                onPlayerClick={setSelectedPlayer}
                            />
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h5" sx={{ flex: 1 }}>Recent teams</Typography>
                                <Button onClick={() => navigate('/virtual-sports/teams')}>View all</Button>
                            </Stack>
                            <VirtualTeamsTable
                                teams={teams}
                                sportNames={new Map(sports.map(sport => [sport.id, sport.name]))}
                                onTeamClick={setSelectedTeam}
                            />
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h5" sx={{ flex: 1 }}>
                                    Recently created matches
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                        ({totalMatches.toLocaleString()} total)
                                    </Typography>
                                </Typography>
                                <Button onClick={() => navigate('/virtual-sports/matches')}>View all</Button>
                            </Stack>
                            {openError ? <Alert severity="warning" sx={{ mb: 2 }} onClose={clearError}>{openError}</Alert> : null}
                            <VirtualMatchesTable
                                matches={matches}
                                onOpenMatch={openMatch}
                                openingSyncId={openingSyncId}
                                onEditMatch={setEditingMatch}
                            />
                        </CardContent>
                    </Card>
                </>
            )}
            <VirtualSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
            <VirtualActionsDialog open={actionsOpen} onClose={() => setActionsOpen(false)} />
            <VirtualStatisticsDialog open={statisticsOpen} onClose={() => setStatisticsOpen(false)} />
            <VirtualPlayerDetailsDialog
                player={selectedPlayer}
                sportNames={new Map(sports.map(sport => [sport.id, sport.name]))}
                onClose={() => setSelectedPlayer(null)}
                onUpdated={updated => {
                    setPlayers(current => current.map(player => player.id === updated.id ? updated : player));
                    setSelectedPlayer(updated);
                }}
                onDeleted={playerId => {
                    setPlayers(current => current.filter(player => player.id !== playerId));
                    setSelectedPlayer(null);
                }}
            />
            <VirtualTeamDetailsDialog
                team={selectedTeam}
                sportNames={new Map(sports.map(sport => [sport.id, sport.name]))}
                onClose={() => setSelectedTeam(null)}
                onUpdated={updated => {
                    setTeams(current => current.map(team => team.id === updated.id ? updated : team));
                    setSelectedTeam(updated);
                }}
            />
            <VirtualLeagueDetailsDialog
                leagueSyncId={selectedLeagueSyncId}
                onClose={() => setSelectedLeagueSyncId(null)}
                onUpdated={updated => setLeagues(current => current.map(league => league.sync_id === updated.id
                    ? { ...league, photo_url: updated.imageUrl }
                    : league))}
            />
            <VirtualMatchEditDialog match={editingMatch} onClose={() => setEditingMatch(null)} />
        </Stack>
    );
}
